import { useState } from "react";
import { HintStatsSummaryI, StatsSummaryI } from "../../../services/stats";
import "./stats-table.css";

type Props = {
    summary: StatsSummaryI;
};

type Timeframe = "allTime" | "lastWeek";

type NumericField = Exclude<keyof HintStatsSummaryI, "entities" | "start_time" | "end_time">;

type Row = { label: string; key: NumericField; duration?: boolean };

type Section = { title: string; rows: Row[] };

const TIMEFRAMES: { label: string; value: Timeframe }[] = [
    { label: "All Time", value: "allTime" },
    { label: "Last Week", value: "lastWeek" },
];

const HIGHLIGHT_CARDS: { label: string; key: NumericField }[] = [
    { label: "Hints Checked Out", key: "hints_checked_out" },
    { label: "Hints Completed", key: "hints_completed" },
];

const SECTIONS: Section[] = [
    {
        title: "Overall",
        rows: [
            { label: "Hints Timed Out", key: "hints_timed_out" },
            { label: "Total Number of Hints", key: "total_number_of_hints" },
            { label: "Average Time per Hint", key: "average_time_per_hint", duration: true },
        ],
    },
    {
        title: "TEH",
        rows: [
            { label: "Checked Out", key: "teh_hints_checked_out" },
            { label: "Completed", key: "teh_hints_completed" },
            { label: "Timed Out", key: "teh_hints_timed_out" },
            { label: "People Added", key: "teh_people_added" },
        ],
    },
    {
        title: "OQH",
        rows: [
            { label: "Checked Out", key: "oqh_hints_checked_out" },
            { label: "Completed", key: "oqh_hints_completed" },
            { label: "Timed Out", key: "oqh_hints_timed_out" },
            { label: "People Added", key: "oqh_people_added" },
        ],
    },
    {
        title: "Pipeline",
        rows: [
            { label: "Hints Queued", key: "hints_queued" },
            { label: "Processed by Pipeline", key: "processed_by_pipeline" },
            { label: "Added by Pipeline", key: "hints_added_by_pipeline" },
            { label: "Completed by Pipeline", key: "hints_completed_by_pipeline" },
            { label: "Bad Hints Removed", key: "bad_hints_removed" },
            { label: "Stale Hints Removed", key: "stale_hints_removed" },
        ],
    },
];

function toNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === "") return null;
    const num = typeof value === "string" ? Number(value) : value;
    return Number.isNaN(num) ? null : num;
}

function hasValue(value: number | string | null | undefined): boolean {
    const num = toNumber(value);
    return num !== null && num !== 0;
}

function formatNumber(value: number | string | null | undefined): string {
    const num = toNumber(value);
    if (num === null) return "—";
    return Number.isInteger(num) ? num.toLocaleString() : num.toFixed(2);
}

function formatDuration(value: number | string | null | undefined): string {
    const seconds = toNumber(value);
    if (seconds === null || seconds <= 0) return "—";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.round(seconds % 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
}

export default function StatsTable({ summary }: Props) {
    const [timeframe, setTimeframe] = useState<Timeframe>("allTime");
    const stats = summary[timeframe];

    return (
        <div id="stats-summary">
            <div id="stats-timeframe-selector">
                {TIMEFRAMES.map(({ label, value }) => (
                    <button
                        key={value}
                        type="button"
                        className={`stats-timeframe-option${timeframe === value ? " selected" : ""}`}
                        onClick={() => setTimeframe(value)}
                    >
                        {label}
                    </button>
                ))}
            </div>

            <div id="stats-highlight-cards">
                {HIGHLIGHT_CARDS.map(({ label, key }) => (
                    <div className="stats-highlight-card" key={key}>
                        <span className="stats-highlight-value">{formatNumber(stats[key])}</span>
                        <span className="stats-highlight-label">{label}</span>
                    </div>
                ))}
            </div>

            {SECTIONS.map((section) => {
                const visibleRows = section.rows.filter((row) => hasValue(stats[row.key]));
                if (visibleRows.length === 0) return null;

                return (
                    <div className="stats-section" key={section.title}>
                        <h2 className="stats-section-title">{section.title}</h2>
                        <table className="stats-summary-table">
                            <thead>
                                <tr>
                                    <th>Stat</th>
                                    <th>Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.map(({ label, key, duration }) => {
                                    const format = duration ? formatDuration : formatNumber;
                                    return (
                                        <tr key={key}>
                                            <td>{label}</td>
                                            <td>{format(stats[key])}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                );
            })}
        </div>
    );
}
